'use client';

import * as React from 'react';

import { cn } from '../lib/utils';
import { safeHttpUrl } from '../lib/safe-url';
import { GithubMark } from './icons/brand-icons';

interface GithubLinkButtonProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'children'> {
  href: string;
  /** Shown next to the GitHub mark. Hidden on `< sm` viewports so the pill
   *  collapses to just the icon on mobile. */
  label: string;
}

/**
 * External link to a GitHub repository, folder, or file — rendered as a
 * bordered pill with the GitHub mark. Used by pages that point to a
 * canonical source location in the monorepo (e.g. `View repository`,
 * `View folder`, `View registry`).
 */
export function GithubLinkButton({
  href,
  label,
  className,
  ...props
}: GithubLinkButtonProps) {
  const safeHref = safeHttpUrl(href);

  if (!safeHref) {
    return (
      <span
        className={cn(
          'flex shrink-0 items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium opacity-60',
          className,
        )}
      >
        <GithubMark className="size-5" />
        <span className="hidden sm:inline">{label}</span>
      </span>
    );
  }

  return (
    <a
      href={safeHref}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'group flex shrink-0 items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium transition-colors',
        'hover:border-[var(--l3-violet)] hover:bg-[var(--l3-violet-soft)] hover:text-[var(--l3-violet)]',
        className,
      )}
      {...props}
    >
      <GithubMark className="size-5" />
      <span className="hidden sm:inline">{label}</span>
    </a>
  );
}

export type { GithubLinkButtonProps };
